import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { DEMO_TOUR_STORAGE_KEY } from '../config/demoTourSteps';

type DemoSessionData = {
  hardExpiresAt?: string;
  remainingHardMs?: number;
  remainingIdleMs?: number;
  hardLimitMinutes?: number;
};

const formatMmSs = (ms: number) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Sticky countdown for the shared provider demo sandbox.
 */
const DemoSessionBanner: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState<DemoSessionData | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const isDemo = !!(user as any)?.isDemo || user?.email === 'demo.provider@illinoislawnhub.com';

  const refresh = useCallback(async () => {
    if (!isDemo || user?.userType !== 'provider') return;
    try {
      const res = await apiService.get('/demo/session');
      if (res.success && res.data) {
        const data = res.data as DemoSessionData;
        setSession(data);
        setRemainingMs(
          Math.min(data.remainingHardMs ?? Infinity, data.remainingIdleMs ?? Infinity)
        );
      }
    } catch (err: any) {
      if (err?.status === 401 || err?.message?.includes('Demo session')) {
        await logout();
        navigate('/demo/exit', { replace: true });
      }
    }
  }, [isDemo, user?.userType, logout, navigate]);

  useEffect(() => {
    if (!isDemo) return;
    refresh();
    const poll = setInterval(refresh, 30000);
    return () => clearInterval(poll);
  }, [isDemo, refresh]);

  useEffect(() => {
    if (remainingMs == null) return;
    const tick = setInterval(() => {
      setRemainingMs((prev) => (prev == null ? prev : Math.max(0, prev - 1000)));
    }, 1000);
    return () => clearInterval(tick);
  }, [session?.hardExpiresAt]);

  useEffect(() => {
    if (remainingMs === 0) {
      (async () => {
        await logout();
        navigate('/demo/exit', { replace: true });
      })();
    }
  }, [remainingMs, logout, navigate]);

  if (!isDemo || user?.userType !== 'provider' || remainingMs == null) return null;

  const hardLimitMs = (session?.hardLimitMinutes || 30) * 60 * 1000;
  const progress = Math.min(100, Math.max(0, (remainingMs / hardLimitMs) * 100));

  const handleReset = async () => {
    setResetting(true);
    try {
      await apiService.post('/demo/reset', {});
      await refresh();
      window.location.reload();
    } catch (err: any) {
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  const handleLogoutOfDemo = async () => {
    setLoggingOut(true);
    try {
      try {
        localStorage.removeItem(DEMO_TOUR_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      // Server logout releases the shared session and reseeds demo data.
      await logout();
      navigate('/demo/exit', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        bgcolor: 'warning.light',
        borderBottom: 1,
        borderColor: 'warning.main',
        px: 2,
        py: 1,
      }}
    >
      <Alert
        severity="warning"
        sx={{ bgcolor: 'transparent', py: 0 }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button color="inherit" size="small" onClick={handleReset} disabled={resetting || loggingOut}>
              {resetting ? 'Resetting…' : 'Reset demo data'}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              startIcon={<Logout />}
              onClick={handleLogoutOfDemo}
              disabled={loggingOut || resetting}
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                boxShadow: 2,
                whiteSpace: 'nowrap',
              }}
            >
              {loggingOut ? 'Logging out…' : 'Log Out of Demo'}
            </Button>
          </Box>
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Demo sandbox — session ends in {formatMmSs(remainingMs)} (max 30 min; idle logout at 10 min)
        </Typography>
        <Typography variant="caption" display="block">
          Sample customers use fictional Demo Township addresses with synthetic map pins for route practice.
          When you’re done, use Log Out of Demo so the next person gets a fresh sandbox.
        </Typography>
      </Alert>
      <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5, height: 4 }} />
    </Box>
  );
};

export default DemoSessionBanner;
